// Commitment form validation + serialization — FE-043.
//
// Pure functions: no React, no side effects. The page calls
// ``validateCommitmentForm`` before invoking the mutation and
// ``serializeCommitmentForm`` to translate the controlled-input
// shape into BE-052's payload (notably ``account_id`` →
// ``client_id``).

import type {
  CommitmentFormValues,
  CreateCommitmentInput,
  UpdateCommitmentInput,
  CommitmentDetail,
} from "./types";


export type CommitmentFormErrors = Partial<
  Record<keyof CommitmentFormValues, string>
>;


export function blankCommitmentFormValues(): CommitmentFormValues {
  return {
    title: "",
    category_slug: "",
    account_id: "",
    plan_year_id: "",
    description: "",
    description_document_id: "",
    priority: "",
    complexity: "",
    due_date_time: "",
    assigned_to: "",
    assigned_job_title: "",
    assigned_client_contact_id: "",
    reviewer_id: "",
    tag_slugs: "",
    visibility: "team",
    source_touchpoint_id: "",
  };
}


export function valuesFromDetail(
  detail: CommitmentDetail,
): CommitmentFormValues {
  return {
    title: detail.title ?? "",
    category_slug: detail.category_slug ?? "",
    account_id: detail.account_id ?? detail.client_id ?? "",
    plan_year_id: detail.plan_year_id ?? "",
    description: detail.description ?? "",
    description_document_id: detail.description_document_id ?? "",
    priority: detail.priority ?? "",
    complexity:
      detail.complexity != null ? String(detail.complexity) : "",
    due_date_time: detail.due_date_time
      ? toLocalDateTimeString(detail.due_date_time)
      : "",
    assigned_to: detail.assigned_to ?? "",
    assigned_job_title: detail.assigned_job_title ?? "",
    assigned_client_contact_id: detail.assigned_client_contact_id ?? "",
    reviewer_id: detail.reviewer_id ?? "",
    tag_slugs: (detail.tags ?? [])
      .map((t) => t.slug)
      .filter((s): s is string => typeof s === "string" && s !== "")
      .join(", "),
    visibility: detail.visibility === "private" ? "private" : "team",
    // PATCH never lets the user reassign the source touchpoint, but
    // we hold the value so the round-trip stays lossless.
    source_touchpoint_id: detail.source_touchpoint_id ?? "",
  };
}


export function validateCommitmentForm(
  values: CommitmentFormValues,
): { errors: CommitmentFormErrors; ok: boolean } {
  const errors: CommitmentFormErrors = {};
  if (values.title.trim().length === 0) {
    errors.title = "Title is required.";
  }
  if (values.complexity !== "") {
    const n = Number(values.complexity);
    if (!Number.isInteger(n) || ![1, 2, 3, 5].includes(n)) {
      errors.complexity = "Complexity must be 1, 2, 3, or 5.";
    }
  }
  // Assignment XOR — only one of the three targets may be set.
  const targets = [
    values.assigned_to.trim(),
    values.assigned_job_title.trim(),
    values.assigned_client_contact_id.trim(),
  ].filter((v) => v !== "");
  if (targets.length > 1) {
    const msg =
      "Pick one assignment target (user, job title, or Account contact).";
    if (values.assigned_to.trim() !== "") errors.assigned_to = msg;
    if (values.assigned_job_title.trim() !== "") {
      errors.assigned_job_title = msg;
    }
    if (values.assigned_client_contact_id.trim() !== "") {
      errors.assigned_client_contact_id = msg;
    }
  }
  return { errors, ok: Object.keys(errors).length === 0 };
}


export function serializeCommitmentForm(
  values: CommitmentFormValues,
): CreateCommitmentInput {
  return {
    title: values.title.trim(),
    category_slug: values.category_slug.trim(),
    client_id: emptyToUndefined(values.account_id),
    plan_year_id: emptyToUndefined(values.plan_year_id),
    description: emptyToUndefined(values.description),
    description_document_id: emptyToUndefined(values.description_document_id),
    priority: values.priority === "" ? undefined : values.priority,
    complexity: parseComplexity(values.complexity),
    due_date_time: emptyToUndefined(values.due_date_time),
    assigned_to: emptyToUndefined(values.assigned_to),
    assigned_job_title: emptyToUndefined(values.assigned_job_title),
    assigned_client_contact_id:
      emptyToUndefined(values.assigned_client_contact_id),
    reviewer_id: emptyToUndefined(values.reviewer_id),
    tag_slugs: parseTagSlugs(values.tag_slugs),
    visibility: values.visibility === "private" ? "private" : "team",
    source_touchpoint_id: emptyToUndefined(values.source_touchpoint_id),
  };
}


/** PATCH variant — only emits keys that the user actually filled in,
 * because a blank input on edit means "leave alone" rather than
 * "clear the field". */
export function serializeCommitmentFormPatch(
  values: CommitmentFormValues,
): UpdateCommitmentInput {
  const out: UpdateCommitmentInput = {};
  if (values.title.trim()) out.title = values.title.trim();
  if (values.category_slug.trim()) out.category_slug = values.category_slug.trim();
  if (values.account_id.trim()) out.client_id = values.account_id.trim();
  if (values.plan_year_id.trim()) out.plan_year_id = values.plan_year_id.trim();
  if (values.description !== "") out.description = values.description;
  if (values.priority !== "") out.priority = values.priority;
  const complexity = parseComplexity(values.complexity);
  if (complexity !== undefined) out.complexity = complexity;
  if (values.due_date_time !== "") out.due_date_time = values.due_date_time;
  if (values.assigned_to.trim()) out.assigned_to = values.assigned_to.trim();
  if (values.assigned_job_title.trim()) {
    out.assigned_job_title = values.assigned_job_title.trim();
  }
  if (values.assigned_client_contact_id.trim()) {
    out.assigned_client_contact_id =
      values.assigned_client_contact_id.trim();
  }
  if (values.reviewer_id.trim()) out.reviewer_id = values.reviewer_id.trim();
  const slugs = parseTagSlugs(values.tag_slugs);
  if (slugs && slugs.length > 0) out.tag_slugs = slugs;
  // Visibility is always emitted on PATCH so the user can flip
  // Private back to Team-visible — omitting it would leave the row
  // stuck Private after the toggle.
  out.visibility = values.visibility === "private" ? "private" : "team";
  return out;
}


function emptyToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}


function parseComplexity(raw: string): number | undefined {
  if (raw.trim() === "") return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return undefined;
  if (![1, 2, 3, 5].includes(n)) return undefined;
  return n;
}


function parseTagSlugs(raw: string): string[] | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  return trimmed
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
}


function toLocalDateTimeString(iso: string): string {
  // ``<input type="datetime-local">`` wants ``YYYY-MM-DDTHH:MM``;
  // strip the seconds + timezone from a backend ISO timestamp.
  const idxColon = iso.indexOf(":");
  if (idxColon === -1) return iso;
  // Find the "T" then take 16 chars (10 date + T + 5 hh:mm).
  const t = iso.indexOf("T");
  if (t === -1) return iso;
  return iso.slice(0, t + 6);
}
